/**
 * OnboardingMentorProvider — Phase 4 foundation.
 *
 * Responsibilities in V1:
 *   - Gate the entire mentor behind `VITE_ONBOARDING_EDITOR_V1`. When the
 *     flag is off, this component renders only its children — zero render
 *     cost, zero side effects.
 *   - Load + persist a per-user session via localStorage.
 *   - Expose start / pause / resume / skip / dismiss / restart / advance
 *     methods to descendants via `useOnboardingMentor()`.
 *   - Emit lifecycle analytics.
 *
 * Explicitly NOT in Phase 4:
 *   - Journey step definitions (e0–e10) → Phase 5/6.
 *   - The actual overlay UI for each step → wired by `useEditorMentor`
 *     in Phase 5.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../auth/useAuth";
import { emit, nowIso } from "./analytics/events";
import {
  clearSession,
  createDebouncedSaver,
  isMentorDisabledLocally,
  loadSession,
} from "./persistence/storage";
import {
  createInitialSession,
  trimStepHistory,
  type EditorStepId,
  type OnboardingSessionState,
  type SessionStatus,
  type StepExitReason,
} from "./persistence/schema";

function isFeatureFlagEnabled(): boolean {
  try {
    const env =
      (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
    return env.VITE_ONBOARDING_EDITOR_V1 === "1";
  } catch {
    return false;
  }
}

function generateSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `ob-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

interface RestartOptions {
  from: "profile" | "editor_top_bar";
  branch?: "blank" | "template";
  templateId?: string | null;
}

interface StartOptions {
  branch: "blank" | "template";
  templateId: string | null;
}

export interface OnboardingMentorApi {
  enabled: boolean;
  session: OnboardingSessionState | null;

  start: (options: StartOptions) => void;
  pause: () => void;
  resume: () => void;
  skip: () => void;
  dismiss: () => void;
  restart: (options: RestartOptions) => void;

  advanceTo: (stepId: EditorStepId) => void;
  completeStep: (stepId: EditorStepId, exitReason: StepExitReason) => void;
  markCompleted: (outcome: "published" | "finished_without_publish") => void;
}

const noopApi: OnboardingMentorApi = {
  enabled: false,
  session: null,
  start: () => {},
  pause: () => {},
  resume: () => {},
  skip: () => {},
  dismiss: () => {},
  restart: () => {},
  advanceTo: () => {},
  completeStep: () => {},
  markCompleted: () => {},
};

const OnboardingMentorContext = createContext<OnboardingMentorApi>(noopApi);

export function useOnboardingMentor(): OnboardingMentorApi {
  return useContext(OnboardingMentorContext);
}

export interface OnboardingMentorProviderProps {
  children: ReactNode;
}

export function OnboardingMentorProvider({
  children,
}: OnboardingMentorProviderProps) {
  const { session: authSession } = useAuth();
  const userId = authSession?.user?.id ?? null;

  const flagEnabled = isFeatureFlagEnabled();
  const locallyDisabled = isMentorDisabledLocally();
  const active = flagEnabled && !locallyDisabled && Boolean(userId);

  const [state, setState] = useState<OnboardingSessionState | null>(null);
  const saverRef = useRef<ReturnType<typeof createDebouncedSaver> | null>(null);

  // Load persisted session whenever the active user changes.
  useEffect(() => {
    if (!active || !userId) {
      setState(null);
      saverRef.current = null;
      return;
    }
    saverRef.current = createDebouncedSaver();
    const loaded = loadSession(userId);
    setState(loaded);
  }, [active, userId]);

  // Persist on every state change.
  useEffect(() => {
    if (!active) return;
    if (!state) return;
    saverRef.current?.save(state);
  }, [active, state]);

  // Flush pending writes when the tab is hidden so we don't lose late
  // changes if the user closes the editor right after a transition.
  useEffect(() => {
    if (!active) return;
    const flush = () => saverRef.current?.flushNow();
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [active]);

  const mutate = useCallback(
    (
      mutator: (
        prev: OnboardingSessionState | null,
      ) => OnboardingSessionState | null,
    ) => {
      setState((prev) => mutator(prev));
    },
    [],
  );

  const touch = useCallback(
    (s: OnboardingSessionState): OnboardingSessionState => ({
      ...s,
      lastActivityAt: nowIso(),
    }),
    [],
  );

  const start = useCallback(
    ({ branch, templateId }: StartOptions) => {
      if (!userId) return;
      const sessionId = generateSessionId();
      const next: OnboardingSessionState = {
        ...createInitialSession(userId, sessionId),
        status: "active",
        branch,
        templateId,
        startedAt: nowIso(),
        lastActivityAt: nowIso(),
      };
      setState(next);
      emit({
        type: "mentor.started",
        sessionId,
        userId,
        branch,
        templateId,
        ts: nowIso(),
      });
    },
    [userId],
  );

  const pause = useCallback(() => {
    mutate((prev) => {
      if (!prev || prev.status !== "active") return prev;
      emit({
        type: "mentor.paused",
        sessionId: prev.sessionId,
        atStep: prev.currentStep,
        ts: nowIso(),
      });
      return touch({ ...prev, status: "paused" });
    });
  }, [mutate, touch]);

  const resume = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      if (prev.status === "active") return prev;
      const from: SessionStatus = prev.status;
      emit({
        type: "mentor.resumed",
        sessionId: prev.sessionId,
        from,
        ts: nowIso(),
      });
      return touch({ ...prev, status: "active" });
    });
  }, [mutate, touch]);

  const skip = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      emit({
        type: "mentor.skipped",
        sessionId: prev.sessionId,
        atStep: prev.currentStep,
        ts: nowIso(),
      });
      return touch({ ...prev, status: "skipped" });
    });
  }, [mutate, touch]);

  const dismiss = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      emit({
        type: "mentor.dismissed",
        sessionId: prev.sessionId,
        atStep: prev.currentStep,
        ts: nowIso(),
      });
      return touch({ ...prev, status: "dismissed" });
    });
  }, [mutate, touch]);

  const restart = useCallback(
    ({ from, branch, templateId }: RestartOptions) => {
      if (!userId) return;
      const sessionId = generateSessionId();
      emit({
        type: "mentor.restarted",
        sessionId,
        from,
        ts: nowIso(),
      });
      const next: OnboardingSessionState = {
        ...createInitialSession(userId, sessionId),
        status: "active",
        branch: branch ?? "unknown",
        templateId: templateId ?? null,
        startedAt: nowIso(),
        lastActivityAt: nowIso(),
      };
      setState(next);
    },
    [userId],
  );

  const advanceTo = useCallback(
    (stepId: EditorStepId) => {
      mutate((prev) => {
        if (!prev || prev.status !== "active") return prev;
        if (prev.currentStep === stepId) return prev;
        emit({
          type: "step.entered",
          sessionId: prev.sessionId,
          stepId,
          ts: nowIso(),
        });
        return touch({
          ...prev,
          currentStep: stepId,
          stepHistory: trimStepHistory([
            ...prev.stepHistory,
            {
              stepId,
              enteredAt: nowIso(),
              exitedAt: null,
              exitReason: null,
            },
          ]),
        });
      });
    },
    [mutate, touch],
  );

  const completeStep = useCallback(
    (stepId: EditorStepId, exitReason: StepExitReason) => {
      mutate((prev) => {
        if (!prev) return prev;
        const history = prev.stepHistory.slice();
        // Close the most recent open entry for this step, if any.
        for (let i = history.length - 1; i >= 0; i--) {
          if (history[i]!.stepId === stepId && !history[i]!.exitedAt) {
            history[i] = {
              ...history[i]!,
              exitedAt: nowIso(),
              exitReason,
            };
            break;
          }
        }
        const durationMs = (() => {
          for (let i = history.length - 1; i >= 0; i--) {
            const h = history[i]!;
            if (h.stepId === stepId && h.exitedAt) {
              return (
                new Date(h.exitedAt).getTime() - new Date(h.enteredAt).getTime()
              );
            }
          }
          return 0;
        })();
        if (exitReason === "completed") {
          emit({
            type: "step.completed",
            sessionId: prev.sessionId,
            stepId,
            durationMs,
            ts: nowIso(),
          });
        } else if (exitReason === "bypassed") {
          emit({
            type: "step.bypassed",
            sessionId: prev.sessionId,
            stepId,
            reason: "auto_pass",
            ts: nowIso(),
          });
        }
        return touch({ ...prev, stepHistory: trimStepHistory(history) });
      });
    },
    [mutate, touch],
  );

  const markCompleted = useCallback(
    (outcome: "published" | "finished_without_publish") => {
      mutate((prev) => {
        if (!prev) return prev;
        const completedAt = nowIso();
        const durationMs = prev.startedAt
          ? new Date(completedAt).getTime() - new Date(prev.startedAt).getTime()
          : 0;
        emit({
          type: "mentor.completed",
          sessionId: prev.sessionId,
          outcome,
          durationMs,
          ts: completedAt,
        });
        return {
          ...prev,
          status: "completed",
          outcome,
          completedAt,
          lastActivityAt: completedAt,
        };
      });
    },
    [mutate],
  );

  const api = useMemo<OnboardingMentorApi>(() => {
    if (!active) return noopApi;
    return {
      enabled: true,
      session: state,
      start,
      pause,
      resume,
      skip,
      dismiss,
      restart,
      advanceTo,
      completeStep,
      markCompleted,
    };
  }, [
    active,
    state,
    start,
    pause,
    resume,
    skip,
    dismiss,
    restart,
    advanceTo,
    completeStep,
    markCompleted,
  ]);

  // Diagnostic helper for staff. Safe — clears only this user's local key.
  useEffect(() => {
    if (!active || !userId) return;
    (window as unknown as Record<string, unknown>).__staylayerOnboardingReset =
      () => {
        clearSession(userId);
        setState(null);
      };
    return () => {
      delete (window as unknown as Record<string, unknown>)
        .__staylayerOnboardingReset;
    };
  }, [active, userId]);

  return (
    <OnboardingMentorContext.Provider value={api}>
      {children}
    </OnboardingMentorContext.Provider>
  );
}
